import { lazy } from 'react';
import i18next from 'i18next';
import { UsersIcon } from '@shared/ui/icons';
import permissions from '@shared/config/permissions';
import {
  selectManagedUser,
  selectManagedUserFullName,
} from 'redux_store/selectors/userAccountManagementSelectors';

const GlobalUsersManagement = lazy(() => import('@pages/users'));
const UserAccountsManagement = lazy(() => import('@pages/user-accounts-management'));

// Same race guard pattern as resolvePatientLabel: the managed-user slice
// only authoritatively names the user in the URL once its uuid matches
// `params.userUuid`. Otherwise we return '' so the breadcrumb widget
// shows a shimmer skeleton until the slice catches up — avoids flashing
// the previous user's name when navigating between users.
const resolveManagedUserLabel = ({ params, state }) => {
  const managed = selectManagedUser(state);
  if (managed && managed.uuid === params.userUuid) {
    return selectManagedUserFullName(state) || '';
  }
  return '';
};

const USER_ROUTE_GLOBAL_MANAGEMENT = {
  path: '/:currentAccountUuid/global-users',
  exact: true,
  additional: {
    component: GlobalUsersManagement,
    protected: true,
    title: i18next.t('titles:globalUsersManagement'),
    icon: UsersIcon,
    permission: permissions.ACCOUNT_USERS_GET,
    breadcrumb: {
      isRoot: true,
      label: () => 'User management',
    },
  },
};

const USER_ROUTE_ACCOUNTS_MANAGEMENT = {
  path: '/:currentAccountUuid/users/:userUuid/manage-accounts',
  exact: true,
  parentRoute: USER_ROUTE_GLOBAL_MANAGEMENT,
  additional: {
    component: UserAccountsManagement,
    protected: true,
    title: i18next.t('titles:manageUserAccounts'),
    permission: permissions.ACCOUNT_USERS_GET,
    breadcrumb: {
      isRoot: false,
      parentPath: '/:currentAccountUuid/global-users',
      label: resolveManagedUserLabel,
    },
  },
};

export const USER_ROUTES = {
  // ------- Global users management ------
  globalManagement: USER_ROUTE_GLOBAL_MANAGEMENT,
  accountsManagement: USER_ROUTE_ACCOUNTS_MANAGEMENT,
};
